// SingleOrderCard.jsx
import {
  Card, CardContent, Typography, Chip, Button, Box, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";
import { useState } from "react";
import AssignModal from "./AssignModal";
import { markOrderReady, cancelOrder } from "../../api/adminApi";

const formatOrderTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const utcStr = dateStr.endsWith("Z") ? dateStr : `${dateStr}Z`;
  return new Date(utcStr).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "numeric",
    day: "numeric", hour: "numeric", minute: "numeric", hour12: true,
  });
};

export default function SingleOrderCard({ order, onViewItems, onRefresh, settledAs }) {
  const [open, setOpen] = useState(false);

  // 👇 NEW: State to control the "Yes/No" Confirmation Dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [isAssignedOptimistic, setIsAssignedOptimistic] = useState(false); // 👈 NEW STATE

  // Cancellation states
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  // 👇 NEW: This function runs ONLY when they click "Yes" in the modal
  const handleConfirmMarkReady = async () => {
    try {
      setMarkingReady(true);
      await markOrderReady(order.order_id);

      setConfirmOpen(false); // Close the dialog on success

      // Refresh the data silently
      if (onRefresh) await onRefresh();
      else window.location.reload();

    } catch (err) {
      console.error(err);
      alert("Failed to mark order ready. Please try again.");
    } finally {
      setMarkingReady(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert("Please enter a cancellation reason.");
      return;
    }
    try {
      setIsCancelling(true);
      await cancelOrder({
        order_id: order.order_id,
        reason: cancelReason.trim()
      });
      setCancelOpen(false);
      setCancelReason("");

      if (onRefresh) await onRefresh();
      else window.location.reload();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || err.response?.data?.message || "Failed to cancel order. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const getModernStatusStyle = (status) => {
    switch (status) {
      case "READY": return { bg: "#DDECC8", text: "#577A34" };
      case "ASSIGNED": return { bg: "#FFF6DE", text: "#F29D17" };
      case "PICKED_UP": return { bg: "#FFF0EE", text: "#E85D3F" };
      case "OUT_FOR_DELIVERY": return { bg: "#E1F0FF", text: "#1976D2" };
      case "DELIVERED": return { bg: "#DDECC8", text: "#577A34" };
      case "CANCELLED":
      case "REJECTED": return { bg: "#FFF0EE", text: "#E85D3F" };
      default: return { bg: "#F5F7FA", text: "#A59D96" };
    }
  };

  const statusStyle = getModernStatusStyle(order.status);

  return (
    <>
      <Card elevation={0} sx={{
        borderRadius: "24px", border: "none", bgcolor: "#FFFFFF",
        width: "100%", boxSizing: "border-box",
        ...(settledAs === "single" && {
          border: "2px solid #FFF6DE",
        }),
        boxShadow: "0 10px 24px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: "0 16px 32px rgba(0,0,0,0.15)" },
      }}>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>

          {settledAs === "single" && (
            <Box sx={{
              mb: 2, px: 2, py: 0.8, borderRadius: "10px",
              bgcolor: "#FFF6DE", display: "flex", alignItems: "center", gap: 1
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#F29D17", flexShrink: 0 }} />
              <Typography variant="caption" fontWeight="900" sx={{ color: "#F29D17" }}>
                Batcher ran — no group found → assigned as Single Order
              </Typography>
            </Box>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>

            <Box flex={1} minWidth={0} pr={1}>
              <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", wordBreak: "break-word", lineHeight: 1.2, mb: 0.5 }}>
                Order #{order.order_id}
              </Typography>
              <Typography variant="body2" sx={{ color: "#A59D96", fontWeight: 700 }} mb={0.5} noWrap>
                {formatOrderTime(order.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 600 }} noWrap>
                User ID: <Typography component="span" sx={{ color: "#3A2C23", fontWeight: 800 }}>{order.user_id}</Typography>
              </Typography>

              {order.status === "ASSIGNED" && <Typography variant="caption" sx={{ color: "#F29D17" }} fontWeight="900" display="block" mt={1.5}>🚚 Assigned to delivery boy</Typography>}
              {order.status === "PICKED_UP" && <Typography variant="caption" sx={{ color: "#E85D3F" }} fontWeight="900" display="block" mt={1.5}>📦 Picked Up</Typography>}
              {order.status === "OUT_FOR_DELIVERY" && <Typography variant="caption" sx={{ color: "#1976D2" }} fontWeight="900" display="block" mt={1.5}>🚴 Out for delivery</Typography>}
              {order.ready_status !== "READY" && <Typography variant="caption" sx={{ color: "#E85D3F" }} fontWeight="900" display="block" mt={1.5}>⏳ Waiting for vendor</Typography>}
            </Box>

            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1.5} flexShrink={0}>
              <Chip label={order.status} size="small" sx={{
                fontWeight: 800, fontSize: "0.7rem", height: 24, borderRadius: "6px",
                backgroundColor: statusStyle.bg, color: statusStyle.text,
              }} />

              {/* 👇 UPDATED: "View Items" is now a clearly visible soft button 👇 */}
              {order.items?.length > 0 && (
                <Button variant="contained" disableElevation size="small" onClick={() => onViewItems?.(order)}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: "#FFF6DE",       // Soft yellow background
                    color: "#D97706",         // Dark orange text
                    px: 2,
                    py: 0.5,
                    minWidth: 0,
                    fontSize: "0.75rem",
                    border: "1px solid #FFE4B5", // Light border to make it pop
                    "&:hover": { bgcolor: "#F5B71D", color: "#3A2C23", borderColor: "#F5B71D" }
                  }}>
                  View Items
                </Button>
              )}

              {/* 👇 UPDATED: Clicking this opens the confirmation modal 👇 */}
              {order.ready_status !== "READY" && (
                <Button variant="outlined" size="small" disabled={markingReady} onClick={() => setConfirmOpen(true)}
                  sx={{
                    borderRadius: "12px", textTransform: "none", fontWeight: 900,
                    bgcolor: "#DDECC8", color: "#577A34", borderColor: "#CCE3AF", px: 2,
                    "&:hover": { bgcolor: "#CCE3AF", borderColor: "#577A34" }
                  }}>
                  {markingReady ? <CircularProgress size={20} color="inherit" /> : "Mark Ready"}
                </Button>
              )}

              {/* 👇 ADDED `!isAssignedOptimistic` to the condition 👇 */}
              {order.ready_status === "READY" && order.status === "READY" && !isAssignedOptimistic && (
                <Button variant="contained" size="small" disableElevation onClick={() => setOpen(true)}
                  sx={{
                    borderRadius: "12px", textTransform: "none", fontWeight: 900,
                    bgcolor: "#F8C332", color: "#3A2C23", px: 2.5,
                    boxShadow: "0 4px 12px rgba(248,195,50,0.3)",
                    "&:hover": { bgcolor: "#F5B71D" }
                  }}>
                  Assign
                </Button>
              )}

              {/* Cancellation Button */}
              {order.status !== "DELIVERED" && order.status !== "CANCELLED" && (
                <Button variant="outlined" size="small" onClick={() => setCancelOpen(true)}
                  sx={{
                    borderRadius: "12px", textTransform: "none", fontWeight: 900,
                    bgcolor: "#FFF0EE", color: "#E85D3F", borderColor: "#F5D0C9", px: 2,
                    "&:hover": { bgcolor: "#F5D0C9", borderColor: "#E85D3F" }
                  }}>
                  Cancel
                </Button>
              )}
            </Box>
          </Stack>

          <AssignModal
            open={open}
            setOpen={setOpen}
            singleOrder
            orderId={order.order_id}
            onSuccess={async () => {
              setIsAssignedOptimistic(true); // 👈 INSTANTLY hides the button
              if (onRefresh) await onRefresh();
              else window.location.reload();
            }}
          />
        </CardContent>
      </Card>

      {/* 👇 NEW: BEAUTIFUL YES/NO CONFIRMATION DIALOG 👇 */}
      <Dialog
        open={confirmOpen}
        onClose={() => !markingReady && setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 2,
            minWidth: "320px",
            textAlign: "center",
            boxShadow: "0 10px 32px rgba(0,0,0,0.2)"
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "900",
            color: "#3A2C23",
            fontSize: "1.4rem",
            pb: 1
          }}
        >
          Mark as Ready?
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" fontWeight="700" color="#6E655E" mb={1}>
            Are you sure you want to manually mark Order #{order.order_id} as READY?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          {/* NO BUTTON */}
          <Button
            variant="outlined"
            disabled={markingReady}
            onClick={() => setConfirmOpen(false)}
            sx={{
              borderRadius: "12px",
              color: "#6E655E",
              borderColor: "#ECE5DA",
              fontWeight: "900",
              px: 4,
              py: 1,
              "&:hover": { bgcolor: "#FFFDF8", borderColor: "#A59D96" }
            }}
          >
            No
          </Button>

          {/* YES BUTTON */}
          <Button
            variant="contained"
            disableElevation
            disabled={markingReady}
            onClick={handleConfirmMarkReady}
            sx={{
              borderRadius: "12px",
              bgcolor: "#DDECC8",
              color: "#577A34",
              fontWeight: "900",
              px: 4,
              py: 1,
              "&:hover": { bgcolor: "#CCE3AF" },
              "&.Mui-disabled": { bgcolor: "#F5F7FA", color: "#A59D96" }
            }}
          >
            {markingReady ? <CircularProgress size={20} color="inherit" /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog
        open={cancelOpen}
        onClose={() => !isCancelling && setCancelOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 2,
            minWidth: "320px",
            boxShadow: "0 10px 32px rgba(0,0,0,0.2)"
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23", fontSize: "1.4rem", pb: 1, textAlign: "center" }}>
          Cancel Order #{order.order_id}?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" fontWeight="700" color="#6E655E" mb={2} textAlign="center">
            Please provide a reason for cancelling this order.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="e.g. Customer requested cancellation via phone call."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={isCancelling}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontWeight: 600,
                color: "#3A2C23",
                "& fieldset": { borderColor: "#ECE5DA", borderWidth: "2px" },
                "&:hover fieldset": { borderColor: "#A59D96" },
                "&.Mui-focused fieldset": { borderColor: "#E85D3F" }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            variant="outlined"
            disabled={isCancelling}
            onClick={() => setCancelOpen(false)}
            sx={{
              borderRadius: "12px", color: "#6E655E", borderColor: "#ECE5DA", fontWeight: "900", px: 4, py: 1,
              "&:hover": { bgcolor: "#FFFDF8", borderColor: "#A59D96" }
            }}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={isCancelling || !cancelReason.trim()}
            onClick={handleCancelOrder}
            sx={{
              borderRadius: "12px", bgcolor: "#E85D3F", color: "#FFFFFF", fontWeight: "900", px: 4, py: 1,
              "&:hover": { bgcolor: "#D84A2C" },
              "&.Mui-disabled": { bgcolor: "#F5F7FA", color: "#A59D96" }
            }}
          >
            {isCancelling ? <CircularProgress size={20} color="inherit" /> : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}